import {
    GroupType,
    IssueMachineHealthResponse,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { useNotificationContext } from 'contexts/NotificationContext';
import { dataPlatformClient, sdClient } from 'contexts/SDKClientContext';
import {
    loadGroupBinds,
    loadMetricBinds,
} from 'custom_hooks/data_hooks/activity/useActivityEventBinds';
import {
    mapDataPlatformIndicatorToGeneralIndicatorInfo,
    mapPromIndicatorToGeneralIndicatorInfo,
} from 'custom_hooks/data_hooks/indicator/shared';
import { subDays } from 'date-fns';
import { getChartMeta } from 'pages/Desk/issues/chart_data/shared';
import { clearURLQueryParams } from 'pages/workbench/shared';
import { useWorkbenchValues } from 'pages/workbench/state';
import { useCallback } from 'react';
import { hierarchyContainsGroupId } from 'utils/group_hierarchy';
import { promiseAllBatched } from 'utils/promise';
import { TrendQueryParams, WorkbenchTrendUrlAction } from 'utils/RouteHelper';

const GuidRegex = new RegExp('[0-9a-fA-F]{8}-(?:[0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}');
const TreeItemQueryBatchSize = 10;

export function useLoadWorkbenchStateFromParams({ systemId }: { systemId: string }) {
    const { value: timeSeriesCutoverPhase2 } = useFeatureFlag('timeSeriesCutoverPhase2');
    const { showToast } = useNotificationContext();

    const {
        setTimeRange,
        setIndicatorTypes,
        updateFromSession,
        updateFromIndicator,
        updateFromAlert,
        updateFromActivityEvent,
        updateFromTreeItem,
        updateFromIssue,
        updateFromChartData,
        setPendingAcknowledgeAsset,
    } = useWorkbenchValues((state) => ({
        setTimeRange: state.setTimeRange,
        setIndicatorTypes: state.setIndicatorTypes,
        updateFromSession: state.updateFromSession,
        updateFromIndicator: state.updateFromIndicator,
        updateFromAlert: state.updateFromAlert,
        updateFromActivityEvent: state.updateFromActivityEvent,
        updateFromTreeItem: state.updateFromTreeItem,
        updateFromIssue: state.updateFromIssue,
        updateFromChartData: state.updateFromChartData,
        setPendingAcknowledgeAsset: state.setPendingAcknowledgeAsset,
    }));

    const { mutateAsync: loadSession } = useMutation({
        meta: { name: 'load-session' },
        mutationFn: async (sessionId: string) => {
            const session = await sdClient.getSession({ id: sessionId });

            if (session.SystemId === systemId) {
                const treeItemQueries = session.GroupIds.map(
                    (groupId) => () => sdClient.getTreeItem({ treeItemId: groupId }),
                );
                const treeItems = await promiseAllBatched(
                    treeItemQueries,
                    TreeItemQueryBatchSize,
                );

                updateFromSession(session, treeItems, timeSeriesCutoverPhase2);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load session' }),
    });

    const { mutateAsync: loadIndicator } = useMutation({
        meta: { name: 'load-indicator' },
        mutationFn: async (indicatorId: string) => {
            // If it's a regular GUID, we assume it's a Prometheus indicator ID.
            if (GuidRegex.test(indicatorId)) {
                const indicator = await sdClient.getIndicator({ indicatorId });
                const isValid = hierarchyContainsGroupId(indicator.Hierarchy, systemId);

                if (isValid) {
                    updateFromIndicator(
                        mapPromIndicatorToGeneralIndicatorInfo(indicator),
                    );
                }
            } else {
                // Otherwise, we assume it's a Data Platform indicator ID and attempt to load it that way.
                const indicator = await dataPlatformClient.indicators.getIndicator({
                    indicatorId,
                });
                const mappedIndicator =
                    mapDataPlatformIndicatorToGeneralIndicatorInfo(indicator);

                const isValid = hierarchyContainsGroupId(
                    mappedIndicator.HierarchyInfo.hierarchy,
                    systemId,
                );

                if (isValid) {
                    updateFromIndicator(mappedIndicator);
                }
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load indicator' }),
    });

    const { mutateAsync: loadGroup } = useMutation({
        meta: { name: 'load-group' },
        mutationFn: async (groupId: string) => {
            const treeItem = await sdClient.getTreeItem({ treeItemId: groupId });
            const isValid =
                treeItem.Id === systemId ||
                hierarchyContainsGroupId(treeItem.Hierarchy, systemId);
            if (isValid) {
                updateFromTreeItem(treeItem);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load group' }),
    });

    const { mutateAsync: loadIssue } = useMutation({
        meta: { name: 'load-issue' },
        mutationFn: async (issueId: string) => {
            const issue = await sdClient.getIssue({ id: issueId });
            const isValid =
                issue instanceof IssueMachineHealthResponse &&
                (issue.Group?.GroupId === systemId ||
                    hierarchyContainsGroupId(issue.Hierarchy, systemId));
            if (isValid) {
                updateFromIssue(issue);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load issue' }),
    });

    const { mutateAsync: loadChartData } = useMutation({
        meta: { name: 'load-chart-data' },
        mutationFn: async ({
            issueId,
            chartDataId,
        }: {
            issueId: string;
            chartDataId: string;
        }) => {
            const chartData = await sdClient.getIssueChartData({ id: chartDataId });
            const chartMeta = await getChartMeta(chartData.ChartData);

            const issue = await sdClient.getIssue({ id: issueId });
            const isValid =
                issue instanceof IssueMachineHealthResponse &&
                (issue.Group?.GroupId === systemId ||
                    hierarchyContainsGroupId(issue.Hierarchy, systemId));

            if (isValid) {
                updateFromChartData(issue, chartMeta);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () => showToast({ type: 'error', content: 'Failed to load chart data' }),
    });

    const { mutateAsync: loadAlert } = useMutation({
        meta: { name: 'load-alert' },
        mutationFn: async (alertId: string) => {
            const alert = await sdClient.getAlert({ alertId });
            const isValid = hierarchyContainsGroupId(alert.Hierarchy, systemId);

            if (isValid) {
                updateFromAlert(alert);
            }
        },

        onSuccess: clearURLQueryParams,
        onError: () => showToast({ type: 'error', content: 'Failed to load alert' }),
    });

    const { mutateAsync: loadAcknowledge } = useMutation({
        meta: { name: 'load-acknowledge' },
        mutationFn: async (alertId: string) => {
            const alert = await sdClient.getAlert({ alertId });
            const isValid = hierarchyContainsGroupId(alert.Hierarchy, systemId);

            if (!isValid) {
                showToast({
                    type: 'error',
                    content: "This alert isn't on the current system.",
                });
                return;
            }

            updateFromAlert(alert);

            const assetId = alert.Hierarchy.find(
                ({ HierarchyType }) => HierarchyType === GroupType.Asset,
            )?.HierarchyId;

            if (!assetId) {
                showToast({
                    type: 'error',
                    content: "Couldn't find the asset for this alert.",
                });
                return;
            }

            const asset = await sdClient.getAsset({ assetId });
            setPendingAcknowledgeAsset(asset);
        },

        onSuccess: clearURLQueryParams,
        onError: () =>
            showToast({ type: 'error', content: 'Failed to open acknowledge dialog' }),
    });

    const { mutateAsync: loadActivityEvent } = useMutation({
        meta: { name: 'load-activity-event' },
        mutationFn: async (activityEventId: string) => {
            const activityEvent = await sdClient.getActivityEvent({ activityEventId });
            const treeItems = await loadGroupBinds(activityEvent.Binds);
            const indicators = await loadMetricBinds(activityEvent.Binds);

            const isValid =
                treeItems.every(({ Hierarchy }) =>
                    hierarchyContainsGroupId(Hierarchy, systemId),
                ) &&
                indicators.every(({ Hierarchy }) =>
                    hierarchyContainsGroupId(Hierarchy, systemId),
                );

            if (isValid) {
                updateFromActivityEvent(activityEvent, treeItems, indicators);
            }
        },

        onSuccess: clearURLQueryParams,

        onError: () =>
            showToast({ type: 'error', content: 'Failed to load activity event' }),
    });

    const loadFromQueryParams = useCallback(
        async (params: TrendQueryParams) => {
            // Clear before routing so only an explicit `?action=acknowledge` re-opens the modal.
            setPendingAcknowledgeAsset(undefined);

            await (() => {
                if (params.sessionId) {
                    return loadSession(params.sessionId);
                }
                if (params.alertId) {
                    if (params.action === WorkbenchTrendUrlAction.Acknowledge) {
                        return loadAcknowledge(params.alertId);
                    }
                    return loadAlert(params.alertId);
                }
                if (params.activityEventId) {
                    return loadActivityEvent(params.activityEventId);
                }
                if (params.indicatorId) {
                    return loadIndicator(params.indicatorId);
                }
                if (params.groupId) {
                    return loadGroup(params.groupId);
                }
                if (params.chartDataId) {
                    if (!params.issueId) {
                        showToast({
                            type: 'error',
                            content:
                                'Invalid URL. Please include an issueId in the URL when attempting to navigate to chart data.',
                        });

                        return Promise.resolve();
                    }

                    return loadChartData({
                        issueId: params.issueId,
                        chartDataId: params.chartDataId,
                    });
                }
                if (params.issueId) {
                    return loadIssue(params.issueId);
                }
                return Promise.resolve();
            })();

            if (params.startDate) {
                setTimeRange({
                    id: 'custom',
                    type: 'custom',
                    startTimestamp: subDays(new Date(params.startDate), 1).valueOf(),
                    endTimestamp: new Date().valueOf(),
                });
            }

            if (params.indicatorTypes) {
                const validCodes = new Set(Object.values(MetricTypeCodes));
                const types = params.indicatorTypes
                    .split(',')
                    .filter((code): code is MetricTypeCodes =>
                        validCodes.has(code as MetricTypeCodes),
                    );
                if (types.length > 0) {
                    setIndicatorTypes(types);
                }
            }
        },
        [
            loadSession,
            loadAlert,
            loadAcknowledge,
            loadActivityEvent,
            loadIndicator,
            loadGroup,
            loadChartData,
            showToast,
            loadIssue,
            setTimeRange,
            setIndicatorTypes,
            setPendingAcknowledgeAsset,
        ],
    );

    return { loadFromQueryParams };
}
